import {
  POSTHOG_FEATURE_FLAGS,
  useAuthDispatch,
  useAuthState,
  useMutation,
  usePostHogFeatureFlag
} from '@percent/cause-dashboard/common/hooks'
import { Trans, useTranslation } from 'react-i18next'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useQuery } from '@percent/cause-dashboard/common/hooks/useQuery/useQuery'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorView, Loader, TitleCard } from '@percent/cause-dashboard/common/components'
import { SET_BANK_DETAILS } from '@percent/cause-dashboard/context/auth'
import { BankDetailsProps } from './BankDetails.types'
import { ProofBankSuccessModal } from './modal/ProofBankSuccessModal'
import { ProofBankErrorModal } from './modal/ProofBankErrorModal'
import { BankDetailsReview } from './bankDetailsReview/BankDetailsReview'
import styles from './BankDetails.module.scss'
import {
  BankDataView,
  BankDetailsReadView,
  BankDetailStatus,
  BankDetailsValidationResult,
  BankDetailsView,
  computeBankDetailsFormValidity,
  useAddBankDetailsForm,
  WireTransferType
} from '@percent/domain/bank-details'
import { Alert, Button, Text } from '@percent/lemonade'
import styled from 'styled-components'
import { BankDetailsValidationPayload } from '@percent/cause-dashboard/services/bankDetails/bankDetails.types'
import axios from 'axios'

const StyledSubmitButtonContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: end;
`

const SUBMITTED_STATUSES = [BankDetailStatus.PENDING, BankDetailStatus.APPROVED]

export function BankDetails({ verifiedCauseOwner, organisationId, sectionRef }: Readonly<BankDetailsProps>) {
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState<File>()
  const [openProofBankSuccessModal, setOpenProofBankSuccessModal] = useState(false)
  const [openProofBankErrorModal, setOpenProofBankErrorModal] = useState(false)
  const [isWaitingReview, setIsWaitingReview] = useState<boolean>(false)
  const [isAfterReview, setIsAfterReview] = useState<boolean>(false)
  const { bankDetailsService } = useServices()
  const { authDispatch } = useAuthDispatch()
  const { authState } = useAuthState()
  const [isValidated, setIsValidated] = useState(false)
  const [savedValues, setSavedValues] = useState<(string | undefined)[]>([])
  const [bankData, setBankData] = useState<BankDetailsValidationResult | undefined>(undefined)
  const [isFileValid, setIsFileValid] = useState(false)
  const [documentDateConfirmed, setDocumentDateConfirmed] = useState(false)
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false)
  const pendingSubmitEventRef = useRef<React.FormEvent<HTMLFormElement> | undefined>(undefined)
  const isPendingSubmissionRef = useRef<boolean>(false)
  const bankDetailsValidation = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_BANK_DETAILS_VALIDATION)
  const bankDetailsValidationWithDocument = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.BANK_DETAILS_VALIDATION_WITH_DOCUMENT
  )

  const bankDataTranslations = useMemo(
    () => ({
      bankName: t('bankDetails.bankName'),
      branchName: t('bankDetails.branchName'),
      addressLine1: t('bankDetails.addressLine1'),
      addressLine2: t('bankDetails.addressLine2'),
      addressLine3: t('bankDetails.addressLine3'),
      city: t('bankDetails.city'),
      state: t('bankDetails.state'),
      postalCode: t('bankDetails.postalCode'),
      country: t('bankDetails.country'),
      title: t('bankDetails.validation.title'),
      description: t('bankDetails.validation.description'),
      foundBankData: t('bankDetails.validation.foundBankData'),
      noDataFound: t('bankDetails.validation.noDataFound'),
      invalidDataTitle: t('bankDetails.validation.invalidDataTitle'),
      invalidDataDescription: t('bankDetails.validation.invalidDataDescription'),
      unknownValidationError: t('bankDetails.validation.unknownError'),
      validDataMessage: t('bankDetails.validation.validDataMessage'),
      validDataTitle: t('bankDetails.validation.validDataTitle'),
      proofOfBankAccountLabel: t('typography.proofBankAccount')
    }),
    [t]
  )

  const [{ isLoading: isFetchBankDetailsLoading }, { apiFunc: fetchBankData }] = useMutation(
    bankDetailsService.fetchBankData,
    async response => {
      const validationResult = response.data.data
      setBankData(validationResult)
      setIsValidated(true)
      // Auto-submit if validation was successful and a submission was pending
      if (validationResult.success && isPendingSubmissionRef.current) {
        setShouldAutoSubmit(true)
      } else {
        isPendingSubmissionRef.current = false
        pendingSubmitEventRef.current = undefined
      }
    },
    async () => {
      setBankData({ success: false, validationErrors: [] })
      setIsValidated(true)
      isPendingSubmissionRef.current = false
      pendingSubmitEventRef.current = undefined
      setShouldAutoSubmit(false)
    }
  )

  const [{ isLoading: isValidationWithDocumentLoading }, { apiFunc: validateBankDetailsWithDocument }] = useMutation(
    bankDetailsService.validateBankDetailsWithDocument,
    async response => {
      const validationResult = response.data.data
      setBankData(validationResult)
      setIsValidated(true)
      // Auto-submit if validation was successful and a submission was pending
      if (validationResult.success && isPendingSubmissionRef.current) {
        setShouldAutoSubmit(true)
      } else {
        isPendingSubmissionRef.current = false
        pendingSubmitEventRef.current = undefined
      }
    },
    async (err: Error) => {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setBankData({
          success: false,
          validationErrors: [
            {
              field: 'uploadDocument',
              message: (err.response?.data as unknown as { error: { message: string } }).error.message
            }
          ]
        })
      } else {
        setBankData({ success: false, validationErrors: [] })
      }
      setIsValidated(true)
      isPendingSubmissionRef.current = false
      pendingSubmitEventRef.current = undefined
      setShouldAutoSubmit(false)
    }
  )

  const isValidationLoading = isFetchBankDetailsLoading || isValidationWithDocumentLoading

  const [{ data: bankDetailsData, isLoading, errorMessage }, { refresh }] = useQuery(
    bankDetailsService.getBankDetails,
    {}
  )
  const handleOpenProofBankErrorModal = () => setOpenProofBankErrorModal(true)
  const handleCloseProofBankErrorModal = () => setOpenProofBankErrorModal(false)
  const handleCloseProofBankSuccessModal = () => {
    refresh()
    setOpenProofBankSuccessModal(false)
  }
  const handleOpenProofBankSuccessModal = () => {
    setIsWaitingReview(true)
    setOpenProofBankSuccessModal(true)
    authDispatch({
      type: SET_BANK_DETAILS,
      payload: {
        orgBankDetails: {
          ...authState.orgBankDetails,
          status: BankDetailStatus.PENDING
        }
      }
    })
  }

  const [{ isLoading: isPostLoading }, { apiFunc }] = useMutation(
    bankDetailsService.postBankDetails,
    () => handleOpenProofBankSuccessModal(),
    () => handleOpenProofBankErrorModal()
  )

  useEffect(() => {
    if (bankDetailsData?.status === BankDetailStatus.PENDING) {
      setIsWaitingReview(true)
    }

    if (bankDetailsData?.status === BankDetailStatus.APPROVED) {
      setIsAfterReview(true)
    }
  }, [bankDetailsData?.status])

  const bankDetailsStatusFlag = !isWaitingReview && !isAfterReview
  const isSubmitted = bankDetailsData?.status ? SUBMITTED_STATUSES.includes(bankDetailsData.status) : false

  const translations = {
    accountHolderName: t('typography.accountHolderName'),
    accountHolderPlaceholder: t('typography.accountHolderPlaceholder'),
    accountHolderDescription: t('typography.accountHolderDescription'),
    helperBankAccountText: t('typography.helperBankAccountText'),
    helperBankAccountLinkText: t('typography.helperBankAccountLinkText'),
    uploadDocumentPlaceholder: t('typography.uploadDocumentPlaceholder'),
    proofBankAccount: t('typography.proofBankAccount'),
    uploadDocument: t('button.uploadDocument'),
    submitBankDocuments: t('button.submitBankDocuments'),
    invalidDataTitle: t('bankDetails.validation.invalidDataTitle'),
    invalidDataDescription: t('bankDetails.validation.invalidDataDescription'),
    unknownValidationError: t('bankDetails.validation.unknownError'),
    documentDateConfirmation: t('typography.documentDateConfirmation')
  }

  const adjustedBankDetailsData = {
    ...bankDetailsData,
    addBankDetailsFormLabels: bankDetailsData?.requiredBankAccountDetails
  }

  const { formik, fields } = useAddBankDetailsForm({
    bankDetailsData: adjustedBankDetailsData,
    t,
    apiFunc,
    selectedFile
  })

  const isFormValid = computeBankDetailsFormValidity({
    hasFormLabels: Boolean(adjustedBankDetailsData?.addBankDetailsFormLabels),
    hasSelectedFile: Boolean(selectedFile),
    formikValid: formik.isValid,
    formikDirty: formik.dirty,
    isPostLoading,
    isFileValid,
    requireDocumentDateConfirmation: true,
    documentDateConfirmed
  })

  const { setFieldError, setFieldTouched, handleSubmit, values } = formik

  useEffect(() => {
    if (bankDetailsValidation && isValidated && Object.values(values).some(value => !savedValues.includes(value))) {
      setIsValidated(false)
      isPendingSubmissionRef.current = false
      pendingSubmitEventRef.current = undefined
      setShouldAutoSubmit(false)
    }
  }, [values, setIsValidated, bankDetailsValidation, isValidated, savedValues])

  const handleSelectedFileChange = useCallback<Dispatch<SetStateAction<File | undefined>>>(file => {
    setSelectedFile(file)
    setDocumentDateConfirmed(false)
  }, [])

  const handleFormValuesChange = useCallback(() => {
    setDocumentDateConfirmed(false)
  }, [])

  useEffect(() => {
    if (shouldAutoSubmit && isValidated) {
      const event = pendingSubmitEventRef.current
      pendingSubmitEventRef.current = undefined
      setShouldAutoSubmit(false)
      handleSubmit(event)
    }
  }, [shouldAutoSubmit, isValidated, handleSubmit])

  const handleSubmitFormWithValidation = (event?: React.FormEvent<HTMLFormElement>) => {
    if (!bankDetailsValidation) {
      return handleSubmit(event)
    }

    const requiredAccountDetails = bankDetailsData?.requiredBankAccountDetails
    const needsValidation = !isValidated && requiredAccountDetails && organisationId

    if (needsValidation) {
      event?.preventDefault()
      isPendingSubmissionRef.current = true
      if (event) {
        pendingSubmitEventRef.current = event
      }

      const { holderName, ...payload } = values

      let paymentDetails: BankDetailsValidationPayload['paymentDetails']

      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      switch (requiredAccountDetails.type) {
        case WireTransferType.IBAN:
          paymentDetails = {
            type: WireTransferType.IBAN,
            iban: payload.iban!,
            bic: payload.bic!
          }
          break
        case WireTransferType.DOMESTIC_WITH_BIC:
          paymentDetails = {
            type: WireTransferType.DOMESTIC_WITH_BIC,
            accountNumber: payload.accountNumber!,
            bic: payload.bic!,
            bankCode: payload.bankCode,
            branchCode: payload.branchCode
          }
          break
        case WireTransferType.DOMESTIC:
          paymentDetails = {
            type: WireTransferType.DOMESTIC,
            accountNumber: payload.accountNumber!,
            bankCode: payload.bankCode!
          }
          break
        default:
          throw new Error('Invalid payment details type')
      }

      if (bankDetailsValidationWithDocument) {
        validateBankDetailsWithDocument({
          organisationId,
          file: selectedFile!,
          bankAccountDetails: {
            holderName: holderName!,
            paymentDetails
          }
        })
      } else {
        fetchBankData({
          organisationId,
          holderName: holderName!,
          paymentDetails
        })
      }
      /* eslint-enable @typescript-eslint/no-non-null-assertion */
    } else {
      handleSubmit(event)
    }

    setSavedValues(Object.values(values))
  }

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const fileFieldText =
    ((selectedFile || (bankDetailsData?.status !== BankDetailStatus.REJECTED && bankDetailsData?.previousFilename)) &&
      selectedFile?.name) ||
    bankDetailsData?.previousFilename

  const isDataValidated = !isValidationLoading && isValidated

  const isBankDataValid =
    bankData?.success === true || (bankData?.success === false && bankData?.validationErrors.length === 0)

  const isValidWithValidation = (isFormValid && isDataValidated && isBankDataValid) || (isFormValid && !isValidated)

  // Keep button disabled during validation, submission, or when auto-submit is pending
  const isProcessing = isValidationLoading || isPostLoading || shouldAutoSubmit
  const isDisabled = isProcessing || isSubmitted || (bankDetailsValidation ? !isValidWithValidation : !isFormValid)

  const bankDetailsContainer = (
    <>
      <ProofBankSuccessModal
        openProofBankSuccessModal={openProofBankSuccessModal}
        handleCloseProofBankSuccessModal={handleCloseProofBankSuccessModal}
      />
      <ProofBankErrorModal
        openProofBankErrorModal={openProofBankErrorModal}
        handleCloseProofBankErrorModal={handleCloseProofBankErrorModal}
      />
      {isWaitingReview && (
        <BankDetailsReview
          reviewBadgeText={t('typography.waitingReviewBadge')}
          reviewText={t('typography.waitingReviewText')}
          reviewLinkText={t('typography.reachOutToUsLink')}
          variant="info"
        />
      )}
      {isAfterReview && (
        <BankDetailsReview
          reviewBadgeText={t('typography.bankDetailsStatusText', {
            status: 'Verified'
          })}
          reviewText={t('typography.afterReviewText')}
          reviewLinkText={t('typography.reachOutToUsLink')}
          variant="success"
        />
      )}

      {bankDetailsStatusFlag && (
        <>
          <Text size="small" fontWeight="regular" className={styles.bankDetailsFormHeader}>
            {t('typography.donationText')}
          </Text>
          <div className={styles.bankDetailsAlertWrapper}>
            <Alert
              variant="info"
              title={
                <Trans
                  i18nKey="typography.whyWeNeedBankDetails"
                  components={{
                    Link: (
                      <a
                        className={styles.bankDetailsLink}
                        href="https://help.goodstack.org/how-do-i-submit-my-bank-details"
                        target="_blank"
                        rel="noreferrer"
                      />
                    )
                  }}
                />
              }
            />
          </div>
        </>
      )}
      {!isWaitingReview && bankDetailsData?.status === 'rejected' && (
        <BankDetailsReview
          variant="error"
          reviewBadgeText={t('typography.bankDetailsStatusText', {
            status: 'Rejected'
          })}
        />
      )}

      {(!bankDetailsData?.bankAccountDetails || !isSubmitted) && (
        <>
          <BankDetailsView
            setSelectedFile={handleSelectedFileChange}
            buttonFlag={!bankDetailsValidation}
            uploadButtonFlag={bankDetailsStatusFlag}
            disableUploadBtn={!verifiedCauseOwner}
            disabledField={isSubmitted}
            bankDetailsData={adjustedBankDetailsData}
            selectedFile={selectedFile}
            isPostLoading={isPostLoading}
            fileFieldText={fileFieldText}
            translations={translations}
            formik={formik}
            fields={fields}
            bankData={bankData}
            isValidated={isValidated}
            onFileChange={() => setIsValidated(false)}
            onValidationFailed={() => setIsFileValid(false)}
            onValidationSuccess={() => setIsFileValid(true)}
            requireDocumentDateConfirmation
            documentDateConfirmed={documentDateConfirmed}
            onDocumentDateConfirmedChange={setDocumentDateConfirmed}
            onFormValuesChange={handleFormValuesChange}
          />
          {bankDetailsValidation && (
            <StyledSubmitButtonContainer>
              <Button
                type="submit"
                variant={'primary'}
                disabled={isDisabled}
                data-testid="add-bank-details-submit-button"
                loading={isPostLoading || isValidationLoading || shouldAutoSubmit}
                onClick={() => handleSubmitFormWithValidation()}
              >
                {t('button.submitBankDocuments')}
              </Button>
            </StyledSubmitButtonContainer>
          )}
        </>
      )}
      {isSubmitted && bankDetailsData?.bankAccountDetails && (
        <BankDetailsReadView
          bankAccountDetails={bankDetailsData.bankAccountDetails}
          translations={translations}
          fileFieldText={fileFieldText}
        />
      )}
    </>
  )

  const bankValidationContainer = (
    <BankDataView
      setFieldError={setFieldError}
      setFieldTouched={setFieldTouched}
      bankData={bankData}
      isValidated={isValidated}
      fields={fields}
      translations={bankDataTranslations}
      variant="body1"
    />
  )

  return (
    <>
      <TitleCard title={t('typography.bankDetails')} size={7}>
        <div ref={sectionRef} />
        {bankDetailsContainer}
      </TitleCard>
      {bankDetailsValidation && (!bankDetailsData?.bankAccountDetails || !isSubmitted) && (
        <TitleCard title={t('bankDetails.validation.title')} size={5}>
          {bankValidationContainer}
        </TitleCard>
      )}
    </>
  )
}
